import { ref, reactive } from 'vue'
import Swal from 'sweetalert2'

// Irmão WootDash-nativo de useAudioRecorder.ts (que continua servindo FocoView.vue sem
// mudança) — só troca a chave numérica (Chatwoot attachment.id) por string (id da própria
// Message, já que o WootDash tem um mediaUrl por mensagem, não um array de anexos).
export interface AudioAttachmentCp { id: string; dataUrl: string }

export interface UseAudioRecorderCpOptions {
  barCount?: number
  exclusivePlayback?: boolean
  onSend: (file: File) => Promise<void>
}

export function useAudioRecorderCp(options: UseAudioRecorderCpOptions) {
  const barCount = options.barCount ?? 22
  const exclusivePlayback = options.exclusivePlayback ?? false

  const audioState = reactive<Record<string, { playing: boolean; current: number; duration: number }>>({})
  const audioEls: Record<string, HTMLAudioElement> = {}

  function ensureAudio(att: AudioAttachmentCp): HTMLAudioElement {
    if (!audioEls[att.id]) {
      const el = new Audio(att.dataUrl)
      audioState[att.id] = { playing: false, current: 0, duration: 0 }
      el.addEventListener('timeupdate', () => { audioState[att.id].current = el.currentTime })
      el.addEventListener('loadedmetadata', () => { audioState[att.id].duration = el.duration })
      el.addEventListener('ended', () => {
        audioState[att.id].playing = false
        audioState[att.id].current = 0
        el.currentTime = 0
      })
      audioEls[att.id] = el
    }
    return audioEls[att.id]
  }

  function toggleAudio(att: AudioAttachmentCp) {
    const el = ensureAudio(att)
    if (audioState[att.id]?.playing) {
      el.pause()
      audioState[att.id].playing = false
    } else {
      if (exclusivePlayback) {
        Object.keys(audioEls).forEach(id => {
          if (id !== att.id && !audioEls[id].paused) {
            audioEls[id].pause()
            if (audioState[id]) audioState[id].playing = false
          }
        })
      }
      el.play().catch(() => { audioState[att.id].playing = false })
      audioState[att.id].playing = true
    }
  }

  function seekAudio(att: AudioAttachmentCp, e: Event) {
    const el = ensureAudio(att)
    const pct = Number((e.target as HTMLInputElement).value)
    if (el.duration) el.currentTime = (pct / 100) * el.duration
  }

  function audioProgress(id: string): number {
    const s = audioState[id]
    return s?.duration ? (s.current / s.duration) * 100 : 0
  }

  function fmtTime(secs: number): string {
    if (!secs || isNaN(secs)) return '0:00'
    const m = Math.floor(secs / 60)
    const s = Math.floor(secs % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  function disposeAudioEls() {
    Object.values(audioEls).forEach(el => { el.pause(); el.src = '' })
  }

  const recording     = ref(false)
  const recordingTime = ref(0)
  const recordedBlob  = ref<Blob | null>(null)
  const recordedUrl   = ref('')
  const waveformBars  = ref<number[]>(new Array(barCount).fill(3))
  let _mr: MediaRecorder | null = null
  let _stream: MediaStream | null = null
  let _recTimer: ReturnType<typeof setInterval> | null = null
  let _animFrame: number | null = null

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      _stream = stream
      const chunks: Blob[] = []
      // OGG/Opus é o formato de voz do WhatsApp; onde o navegador não grava OGG (Chrome/Edge), o WebM é
      // convertido no servidor ao subir o anexo.
      const mimeType = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/mp4', 'audio/webm']
        .find(t => MediaRecorder.isTypeSupported(t)) ?? 'audio/webm'

      const actx = new AudioContext()
      const src  = actx.createMediaStreamSource(stream)
      const anal = actx.createAnalyser()
      anal.fftSize = 64
      src.connect(anal)
      const buf = new Uint8Array(anal.frequencyBinCount)

      const drawFrame = () => {
        anal.getByteFrequencyData(buf)
        waveformBars.value = Array.from({ length: barCount }, (_, i) => {
          const idx = Math.floor(i * buf.length / barCount)
          return Math.max(3, Math.round(buf[idx] / 255 * barCount))
        })
        _animFrame = requestAnimationFrame(drawFrame)
      }
      drawFrame()

      _mr = new MediaRecorder(stream, { mimeType })
      _mr.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data) }
      _mr.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType })
        recordedBlob.value = blob
        if (recordedUrl.value) URL.revokeObjectURL(recordedUrl.value)
        recordedUrl.value = URL.createObjectURL(blob)
        stream.getTracks().forEach(t => t.stop())
        actx.close()
      }
      _mr.start(100)
      recording.value = true
      recordingTime.value = 0
      _recTimer = setInterval(() => recordingTime.value++, 1000)
    } catch {
      Swal.fire({ title: 'Microfone inacessível', text: 'Não foi possível acessar o microfone. Verifique as permissões do navegador.', icon: 'error' })
    }
  }

  function stopRecording() {
    _mr?.stop()
    recording.value = false
    if (_recTimer) { clearInterval(_recTimer); _recTimer = null }
    if (_animFrame) { cancelAnimationFrame(_animFrame); _animFrame = null }
    waveformBars.value = new Array(barCount).fill(3)
  }

  function cancelRecording() {
    if (recording.value) {
      _mr?.stop()
      _stream?.getTracks().forEach(t => t.stop())
      recording.value = false
    }
    if (_recTimer) { clearInterval(_recTimer); _recTimer = null }
    if (_animFrame) { cancelAnimationFrame(_animFrame); _animFrame = null }
    recordedBlob.value = null
    if (recordedUrl.value) { URL.revokeObjectURL(recordedUrl.value); recordedUrl.value = '' }
    waveformBars.value = new Array(barCount).fill(3)
  }

  async function sendRecordedAudio() {
    if (!recordedBlob.value) return
    const type = recordedBlob.value.type
    const ext  = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm'
    const file = new File([recordedBlob.value], `audio_${Date.now()}.${ext}`, { type: recordedBlob.value.type })
    recordedBlob.value = null
    if (recordedUrl.value) { URL.revokeObjectURL(recordedUrl.value); recordedUrl.value = '' }
    await options.onSend(file)
  }

  function fmtRecordingTime(secs: number): string {
    const m = Math.floor(secs / 60)
    const s = secs % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  return {
    audioState, ensureAudio, toggleAudio, seekAudio, audioProgress, fmtTime, disposeAudioEls,
    recording, recordingTime, recordedBlob, recordedUrl, waveformBars,
    startRecording, stopRecording, cancelRecording, sendRecordedAudio, fmtRecordingTime,
  }
}
